import React, { useState } from "react"
import _map from "lodash/map"
import _isEmpty from "lodash/isEmpty"
import { useMembersGroupsQuery } from "@timenotes/shared/src/services/members-groups/queries/use-members-groups-query"
import { useUsersAccountsForFiltersQuery } from "@timenotes/shared/src/services/users-accounts/queries/use-users-accounts-for-filters-query"
import FilterInput from "./filter-input"
import RecordsSelect, { SelectAllLink } from "../records-select"
import { TApiUsersAccount } from "@timenotes/shared/src/services/api-client/types"

export interface TeamFilterProps {
  style?: React.CSSProperties
  width?: string
  values?: TTeamFilterValues
  assignee?: boolean
  noUpperPart?: boolean
  autoFocus?: boolean
  onChange?(values: TTeamFilterValues): void
  onBlur?(): void
  hide?(): void
  placeholder?: string
  filters?: {
    projectId?: string
  }
  noMemberOption?: {
    resourceKey: string,
    name: string,
    id: string,
  }
}

export type TTeamFilterValues = {
  memberHashIds: string[]
  groupHashIds: string[]
}

export const membersToOptions = (
  members: { id: string; firstName: string; lastName: string; hashId: string }[]
) => {
  return _map(members, (member) => {
    return {
      value: {
        resourceKey: `member#${member.hashId}`,
        name: `${member.firstName} ${member.lastName}`,
        id: member.id,
      },
    }
  })
}

const TeamFilter = (props: TeamFilterProps): JSX.Element => {
  const {
    values,
    width,
    onChange,
    onBlur,
    assignee,
    noUpperPart,
    autoFocus,
    hide,
    noMemberOption,
    filters,
    placeholder,
  } = props

  const [showInactive, setShowInactive] = useState(false)

  const membersGroupsQuery = useMembersGroupsQuery({
    perPage: 1000,
    filters: filters,
  })
  const groups = membersGroupsQuery.data?.membersGroups || []

  const usersAccountsQuery = useUsersAccountsForFiltersQuery({
    perPage: 1000,
    filters: filters,
  })
  const members = (usersAccountsQuery.data?.usersAccounts ||
    []) as TApiUsersAccount[]
  const inactiveMembersOptions = usersAccountsQuery.data?.inactiveOptions

  const membersOptions = membersToOptions(members.filter((m) => !!m.active))

  if (!!noMemberOption) {
    membersOptions.unshift({ value: noMemberOption })
  }

  const groupsOptions = _map(groups, (group) => {
    return {
      value: {
        resourceKey: `group#${group.hashId}`,
        name: group.name,
        id: group.id,
      },
    }
  })

  const handleOnChange = (
    items: { name: string; resourceKey: string; id: string }[]
  ): void => {
    if (!onChange) {
      return
    }

    let newTeamFilterValues: TTeamFilterValues = {
      memberHashIds: [],
      groupHashIds: [],
    }

    if (assignee) {
      items.forEach((item) => {
        const splitValue = item.resourceKey.split("#")

        const key = splitValue[0]

        if (key == "member") {
          newTeamFilterValues.memberHashIds.push(item.id)
        } else {
          newTeamFilterValues.groupHashIds.push(item.id)
        }
      })
    } else {
      items.forEach((item) => {
        const splitValue = item.resourceKey.split("#")

        const key = splitValue[0]
        const id = splitValue[1]

        if (key == "member") {
          newTeamFilterValues.memberHashIds.push(id)
        } else {
          newTeamFilterValues.groupHashIds.push(id)
        }
      })
    }

    onChange(newTeamFilterValues)
  }

  const isLoading = usersAccountsQuery.isLoading || membersGroupsQuery.isLoading
  const selectedCount =
    (values?.memberHashIds || []).length + (values?.groupHashIds || []).length

  const inactiveUsersOption = !showInactive
    ? {
        label: (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div>{inactiveMembersOptions?.length} Inactive members hidden</div>
            <div>
              <SelectAllLink onClick={() => setShowInactive(true)}>
                Add to search
              </SelectAllLink>
            </div>
          </div>
        ),
        options: [],
      }
    : {
        label: "Inactive members",
        options: membersToOptions(
          (inactiveMembersOptions || []).map(
            (option) => option.value
          ) as TApiUsersAccount[]
        ),
      }

  const usersOption = !usersAccountsQuery.data?.usersAccounts
    ? []
    : [
        {
          label: "Members",
          allFilters: true,
          options: membersOptions,
        },
      ]

  const groupsOption = !membersGroupsQuery.data?.membersGroups
    ? []
    : [
        {
          label: "Groups",
          allFilters: true,
          options: groupsOptions,
        },
      ]

  const allOptions = [
    ...usersOption,
    ...groupsOption,
    ...((inactiveMembersOptions || []).length > 0 ? [inactiveUsersOption] : []),
  ]

  const selectedItems = allOptions
    .flatMap((optionGroup) => optionGroup.options)
    .flatMap((option) => option.value)
    .filter((value) => {
      const splitValue = value.resourceKey.split("#")

      const key = splitValue[0]
      const hashId = splitValue[1]

      if (assignee) {
        if (key == "member") {
          return (values?.memberHashIds || []).includes(value.id)
        } else {
          return (values?.groupHashIds || []).includes(value.id)
        }
      } else {
        if (key == "member") {
          return (values?.memberHashIds || []).includes(hashId)
        } else {
          return (values?.groupHashIds || []).includes(hashId)
        }
      }
    })

  return (
    <>
      {!noUpperPart && (
        <FilterInput
          placeholder={placeholder ? placeholder : (assignee ? "Assignee" : "Team")}
          count={selectedCount}
        >
          <RecordsSelect<{ name: string; resourceKey: string; id: string }>
            open
            loading={isLoading}
            disabled={isLoading}
            style={{
              width: "300px",
              zIndex: "300",
            }}
            valueProp={assignee ? "id" : "resourceKey"}
            labelProp="name"
            searchProp="name"
            placeholder="Member or Group"
            showSearch={true}
            mode="multiple"
            options={allOptions}
            onChange={handleOnChange}
            onBlur={onBlur}
            value={selectedItems as any}
            autoFocus={true}
            dropdownMatchSelectWidth={true}
            getPopupContainer={(node) => {
              return node
            }}
            filterInputMode={true}
          />
          <div className="options-wrapper"></div>
        </FilterInput>
      )}
      {noUpperPart && (
        <div onClick={(e) => e.stopPropagation()} >
          <RecordsSelect<{ name: string; resourceKey: string; id: string }>
              open={true}
              loading={isLoading}
              disabled={isLoading}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  hide && hide()
                }
              }}
              style={{
                width: "300px",
                zIndex: "300",
              }}
              valueProp={assignee ? "id" : "resourceKey"}
              labelProp="name"
              searchProp="name"
              placeholder="Member or Group"
              showSearch={true}
              mode="multiple"
              options={allOptions}
              onChange={handleOnChange}
              onBlur={onBlur}
              value={selectedItems as any}
              autoFocus={autoFocus}
              dropdownMatchSelectWidth={true}
              getPopupContainer={(node) => {
                return node
              }}
              filterInputMode={true}
            />
        </div>
      )}
    </>
  )
}

export default TeamFilter
